import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Music2 } from "lucide-react";

const STATS = [
  { value: "400.000+", label: "Verkaufte Artikel im TikTok-Shop" },
  { value: "21 MIO+", label: "TikTok-Beitragsaufrufe im August 2026" },
  { value: "195K / 105K", label: "Candy-Peak-Follower auf TikTok / YouTube" },
  { value: "195.000+", label: "Erfolgreiche Bestellungen" },
  { value: "4,3 ★", label: "Aus 9.500+ echten Bewertungen" },
  { value: "TOP 8", label: "TikTok-Jahrescharts 2024" },
];

const FACTS = [
  { label: "BRAND", value: "Candy Peak" },
  { label: "CREATOR", value: "Mario Novembre" },
  { label: "KATEGORIE", value: "Social Commerce" },
  { label: "KANAL", value: "TikTok-Shop + Live" },
];

const PRODUCTS = [
  { src: "/candypeak/fanta.jpg", alt: "Fanta World Bundle – Dosen aus aller Welt" },
  { src: "/candypeak/pringles.jpg", alt: "Pringles World Bundle" },
  { src: "/candypeak/redbull.jpg", alt: "RedBull Glacier Limited Edition" },
  { src: "/candypeak/mystery.jpg", alt: "Candy Peak Mystery Box" },
  { src: "/candypeak/buldak.jpg", alt: "Samyang Buldak Bundle" },
  { src: "/candypeak/zahnstocher.jpg", alt: "CandyPeak Zahnstocher mit Geschmack – Original" },
  { src: "/candypeak/drinks.jpg", alt: "Import Drinks aus aller Welt" },
  { src: "/candypeak/hero.jpg", alt: "Candy Peak Kampagne" },
];

export default function CandyPeak() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden pb-24 pt-32 md:pt-40">
      <div className="mx-auto max-w-[1200px] px-5 md:px-10">
        <Link to="/" data-testid="candypeak-back-link" className="mono-label inline-flex items-center gap-2 text-xs text-white/60 transition-colors hover:text-[var(--crtr-yellow)]">
          <ArrowLeft className="h-4 w-4" /> ZURÜCK
        </Link>

        <p className="mono-label mt-8 text-xs text-[var(--crtr-yellow)]">CASE 03 — SOCIAL COMMERCE))</p>
        <h1 className="font-display mt-4 text-5xl text-white sm:text-6xl lg:text-7xl">CANDY PEAK.</h1>
        <p className="mono-label mt-4 max-w-2xl text-sm text-white/70">
          DER ERFOLGREICHSTE TIKTOK SWEETS- & SNACKSHOP. MIT MARIO NOVEMBRE & TEAM.
        </p>

        {/* campaign hero */}
        <div className="mt-12 overflow-hidden rounded-[2rem] bg-neutral-900" data-testid="candypeak-hero-image">
          <img fetchPriority="high" src="/candypeak/hero.jpg" alt="Candy Peak Kampagne" className="w-full object-cover" />
        </div>

        {/* stats */}
        <div className="mt-14 grid grid-cols-2 gap-x-6 gap-y-10 border-y border-white/10 py-10 md:grid-cols-3" data-testid="candypeak-stats">
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-display text-3xl text-[var(--crtr-yellow)] md:text-4xl">{s.value}</p>
              <p className="mono-label mt-2 text-[10px] text-white/60">{s.label}</p>
            </div>
          ))}
        </div>

        {/* facts */}
        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
          {FACTS.map((f) => (
            <div key={f.label} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <p className="mono-label text-[10px] text-white/50">{f.label}</p>
              <p className="mt-2 font-heavy text-lg uppercase text-white">{f.value}</p>
            </div>
          ))}
        </div>

        {/* story */}
        <div className="mt-16 grid gap-10 md:grid-cols-[1.4fr_1fr] md:items-start">
          <div>
            <h2 className="font-display text-3xl text-white md:text-4xl">TIKTOK MADE ME BUY IT.</h2>
            <div className="mt-6 max-w-xl space-y-4 text-base leading-relaxed text-white/80">
              <p>
                Zusammen mit unserem Creator Mario Novembre und seinem Team wurde mit Candy Peak der
                erfolgreichste TikTok Sweets- & Snackshop kreiert. Außergewöhnliche Produkt-Drops — ob
                Limited Editions von großen globalen Brands oder unique Produkte wie japanische Fanta,
                indische Chips uvm. — treffen den einzigartigen Reiz der NextGen-Konsumenten.
              </p>
              <p>
                Getragen von Mario und befreundeten Creator werden über Social-Media-Channels
                Reichweiten und Attention generiert — mit TikTok-Shop und TikTok-Liveshopping-Sessions
                erzielt Candy Peak regelmäßig neue Rekorde und süße Fans.
              </p>
              <p>
                Von Mystery Boxen über die eigenen CandyPeak-Zahnstocher bis zu Snacks, Chips und Drinks aus
                aller Welt: Jeder Drop wird zum Event — Platz 8 der TikTok-Jahrescharts 2024 und über
                195.000 Bestellungen sprechen für sich.
              </p>
            </div>
          </div>
          <a
            href="https://vm.tiktok.com/ZGdxWJgxH/"
            target="_blank"
            rel="noreferrer"
            data-testid="candypeak-mario-video-link"
            className="group relative block overflow-hidden rounded-[2rem]"
          >
            <img loading="lazy" src="/schlafi/social-mario.jpg" alt="Mario Novembre TikTok-Video" className="aspect-[9/16] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover:bg-black/10">
              <span className="flex flex-col items-center gap-3">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--crtr-yellow)] transition-transform group-hover:scale-110">
                  <Music2 className="h-6 w-6 text-black" />
                </span>
                <span className="mono-label rounded-full bg-black/50 px-3 py-1 text-[10px] text-white backdrop-blur-sm">AUF TIKTOK ANSEHEN</span>
              </span>
            </span>
          </a>
        </div>

        {/* product grid */}
        <h3 className="mono-label mt-16 text-xs text-[var(--crtr-yellow)]">DIE DROPS))</h3>
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="candypeak-product-grid">
          {PRODUCTS.map((p) => (
            <div key={p.src} className="overflow-hidden rounded-2xl bg-white">
              <img loading="lazy" src={p.src} alt={p.alt} className="aspect-square w-full object-cover transition-transform duration-700 hover:scale-105" />
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div className="mt-12 flex flex-wrap gap-4">
          <a
            href="https://candypeak.de/"
            target="_blank"
            rel="noreferrer"
            data-testid="candypeak-shop-link"
            className="group inline-flex items-center gap-3 rounded-full bg-[var(--crtr-yellow)] py-3 pl-6 pr-3"
          >
            <span className="mono-label text-xs text-black">ZUM CANDY PEAK SHOP</span>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black">
              <ExternalLink className="h-4 w-4 text-[var(--crtr-yellow)] transition-transform group-hover:translate-x-0.5" />
            </span>
          </a>
          <a
            href="https://www.tiktok.com/@candypeakgmbh"
            target="_blank"
            rel="noreferrer"
            data-testid="candypeak-tiktok-link"
            className="group inline-flex items-center gap-3 rounded-full border border-[var(--crtr-yellow)] py-3 pl-6 pr-3"
          >
            <span className="mono-label text-xs text-[var(--crtr-yellow)]">@CANDYPEAKGMBH</span>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--crtr-yellow)]">
              <Music2 className="h-4 w-4 text-black" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
